/**
 * cymaticsPipeline — Chladni figures on a vibrating plate.
 *
 * When a square plate is vibrated at certain frequencies, sand on
 * the plate forms intricate patterns where the displacement is zero.
 * The displacement is given by:
 *   z(x, y) = sin(n*pi*x) * sin(m*pi*y) + sin(m*pi*x) * sin(n*pi*y)
 * (or similar). When n != m, complex nodal patterns emerge.
 *
 * We render the absolute value of z as a height field (brighter =
 * higher amplitude), with contours showing the nodal lines. The
 * (n, m) parameters drift over time, cycling through known Chladni
 * patterns. Audio drives the speed of cycling.
 *
 * No simulation, no mesh — pure analytical function. A purely
 * mathematical visualization of wave interference.
 *
 * Audio:
 *   - bass: amplitude of vibration
 *   - mid:  pattern cycling speed
 *   - high: contour sharpness
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CymaticsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Plate coords. The plate fills the screen edge-to-edge on BOTH
  // axes. We pick the LONGER screen dimension and divide by half its
  // size so the corresponding axis spans [-1, +1]. The other axis is
  // scaled by the inverse aspect ratio so the plate reaches both edges.
  // The Chladni frequency range is high (n, m in 8..32) for a dense,
  // high-resolution pattern across the viewport.
  float aspect = uResolution.x / uResolution.y;
  // Long axis: whichever is larger. On landscape, X is the long axis
  // and spans [-1, +1]; on portrait, Y is the long axis.
  // We want p to span [-1, +1] on the long axis and [-1/aspect, +1/aspect]
  // on the short axis. So:
  //   if landscape (aspect > 1): long axis = X. longScale = 0.5 * w / aspect.
  //     wait simpler: we want a fixed scale factor.
  // Long axis gets full scale. Short axis gets scale * aspect (or /aspect).
  float longScale = 0.5 * max(uResolution.x, uResolution.y);
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / longScale;
  // Cycle through Chladni patterns over time.
  // Use floor + fract for smooth interpolation.
  float cycleSpeed = 0.05 + mid * 0.1;
  float ct = uTime * cycleSpeed;
  // Pick a pair (n, m) of integers based on the cycle index.
  // Range 8..32 gives a high-resolution, dense pattern.
  float idx = floor(ct);
  float frac = fract(ct);
  float n1 = 8.0 + 24.0 * abs(fract(idx * 0.6180339887) - 0.5) * 2.0;
  float m1 = 8.0 + 24.0 * abs(fract(idx * 0.3819660113 + 0.7) - 0.5) * 2.0;
  float n2 = 8.0 + 24.0 * abs(fract((idx + 1.0) * 0.6180339887) - 0.5) * 2.0;
  float m2 = 8.0 + 24.0 * abs(fract((idx + 1.0) * 0.3819660113 + 0.7) - 0.5) * 2.0;
  // Smooth interpolation between consecutive patterns.
  float n = mix(n1, n2, smoothstep(0.0, 1.0, frac));
  float m = mix(m1, m2, smoothstep(0.0, 1.0, frac));

  // Chladni wave function: z = cos(n*pi*x) * cos(m*pi*y) - cos(m*pi*x) * cos(n*pi*y).
  // This gives the displacement of a square plate vibrating in the
  // (n, m) mode. The nodal lines (z = 0) form the visible pattern.
  float z = cos(n * 3.14159265 * p.x) * cos(m * 3.14159265 * p.y)
          - cos(m * 3.14159265 * p.x) * cos(n * 3.14159265 * p.y);

  // Amplitude modulation by bass.
  z *= 1.0 + 0.5 * bass;

  // Background: dark plate.
  vec3 bg = vec3(0.04, 0.04, 0.08);

  // Color the displacement:
  //   - Nodal lines (z near 0): bright (where sand would gather)
  //   - High amplitude: dark (where there's no sand)
  vec3 col = bg;

  // Sand at the nodal lines.
  float sand = exp(-z * z * 50.0);
  col = mix(col, vec3(0.95, 0.80, 0.55), sand * 0.9);

  // Glow at the highest positive displacement.
  float posAmp = smoothstep(0.3, 0.8, z);
  col += vec3(0.4, 0.3, 0.6) * posAmp * (0.3 + high * 0.3);

  // Negative displacement (also nodal-area variation).
  float negAmp = smoothstep(-0.8, -0.3, -z);
  col += vec3(0.6, 0.2, 0.4) * negAmp * (0.3 + mid * 0.3);

  // Plate edge fade.
  float edge = 1.0 - max(abs(p.x), abs(p.y));
  edge = smoothstep(0.0, 0.15, edge);
  col *= edge;

  // Subtle pattern label in corner.
  vec2 corner = p - vec2(-0.42, 0.42);
  float labelMask = step(-0.04, corner.x) * step(corner.x, 0.04)
                  * step(-0.005, corner.y) * step(corner.y, 0.005);
  // (skip the actual text rendering — just dim glow)

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeCymaticsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeCymaticsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCymaticsPipeline(gl: WebGL2RenderingContext): CymaticsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCymatics(
  gl: WebGL2RenderingContext,
  pipe: CymaticsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
